import { CustomRouteConfig } from "@/models/plugins/CustomRouterTypes";
import PERMISSIONS from "@/static/permissions";

export const ADMINISTRATION_ROUTE_NAMES = {
  OFFICES: {
    BASIC_INFO: "Administration Offices Basic Information",
    ADDRESS: "Administration Offices Address",
    SETTINGS: "Administration Office Settings",
    OFFICE_PROFILES: "Administration Office Profiles",
    DOCUMENT_REGISTERS: "Administration Office Document Registers",
    CASH_REGISTERS: "Administration Office Cash Registers",
    PAYMENTS: "Administration Office Payments",
    REPORTS: "Administration Office Reports",
    CASHIERS: "Administration Office Cashiers",
    TRADERS: "Administration Office Traders",
    SIGNATURES: "Administration Office Signatures",
  },
  CONTRACTOR: {
    BASIC_DATA: "Administration Contractors Basic Info",
    BANK_ACCOUNT: "Administration Contractors Bank Account",
    PAYER_BANK_ACCOUNT: "Administration Contractors Payer Bank Account",
    SETTINGS: "Administration Contractors Settings",
    EXPORT_AND_INTEGRATION: "Administration Contractors export and integration",
    COMMISSIONS_AND_DISCOUNTS: "Administration Contractors commission and discount",
    EDITING_ATTRIBUTES: "Administration contractors attributes",
    PROJECT_SETTING: "Administration contractors projects",
    CONTRACTOR_CODES: "Administration contractors contractor code",
    AUTO_INVOICE: "Administration contractors automatic invoice",
    STANDING_ORDERS: "Administration contractors standing orders",
    MAIL_NOTIFICATION: "Administration contractors mail notification",
    CUSTOM_EMAIL_CONTENT: "Administration contractors custom email content",
    DOCUMENT_REGISTER: "Administration contractors document register",
    BRANCH: "Administration contractors branch",
    FIXED_ASSETS: "Administration contractor fixed assets",
    ATTRIBUTE_MAPPING: "Administration contractor attribute mapping",
    KSEF: "Administration contractor ksef configuration",
  },
};

const AdministrationRoutes: Array<CustomRouteConfig> = [
  {
    name: "Administration",
    path: "administration",
    component: () => import(/* webpackChunkName: "administration" */ "@/views/administration/AdministrationView.vue"),
    meta: { hasOneOfPermissions: [PERMISSIONS.AdministrationsRead] },
  },
  {
    name: "Administration Booking Files",
    path: "administration/booking-files",
    component: () =>
      import(/* webpackChunkName: "administration" */ "@/views/administration/AdministrationBookingFilesView.vue"),
    meta: { hasOneOfPermissions: [PERMISSIONS.AdministrationBookingsFilesRead] },
  },
  {
    name: "Administration Attributes",
    path: "administration/attributes",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/views/administration/servicesAndAttributes/AdministrationAttributesView.vue"
      ),
    meta: { hasOneOfPermissions: [PERMISSIONS.AdministrationAttributeRead] },
  },
  {
    name: "Administration Attribute Templates",
    path: "administration/service-attributes",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/views/administration/servicesAndAttributes/AdministrationAttributeTemplatesView.vue"
      ),
    meta: {
      hasOneOfPermissions: [PERMISSIONS.AdministrationServiceTypesServicesRead],
    },
  },
  {
    name: "Administration Service Groups",
    path: "administration/service-groups",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/views/administration/servicesAndAttributes/AdministrationServiceGroupsView.vue"
      ),
    meta: {
      hasOneOfPermissions: [PERMISSIONS.AdministrationServiceTypesServicesRead],
    },
  },
  {
    name: "Administration Service Types",
    path: "administration/service-types",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/views/administration/servicesAndAttributes/AdministrationServiceTypesView.vue"
      ),
    meta: {
      hasOneOfPermissions: [PERMISSIONS.AdministrationServiceTypesServicesRead],
    },
  },
  {
    name: "Administration Service Packages",
    path: "administration/service-packages",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/views/administration/servicesAndAttributes/AdministrationServicePackagesView.vue"
      ),
    meta: {
      hasOneOfPermissions: [PERMISSIONS.AdministrationServiceTypesServicesRead],
    },
  },
  {
    name: "Administration Cost Hints",
    path: "administration/cost-hints",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/views/administration/servicesAndAttributes/AdministrationCostHintsView.vue"
      ),
    meta: {
      hasOneOfPermissions: [PERMISSIONS.CostHintRead],
    },
  },
  {
    name: "Administration Users",
    path: "administration/users",
    component: () =>
      import(/* webpackChunkName: "administration" */ "@/views/administration/AdministrationUsersView.vue"),
    meta: { hasOneOfPermissions: [PERMISSIONS.AdministrationUsersRead] },
  },
  {
    name: "Administration Roles",
    path: "administration/roles",
    component: () =>
      import(/* webpackChunkName: "administration" */ "@/views/administration/AdministrationRolesView.vue"),
    meta: { hasOneOfPermissions: [PERMISSIONS.AdministrationRolesRead] },
  },
  {
    name: "Administration Contractors",
    path: "administration/contractors",
    component: () =>
      import(
        /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsView.vue"
      ),
    children: [
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.BASIC_DATA,
        path: "basic",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsDataView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.BANK_ACCOUNT,
        path: "accounts",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsBankAccountView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.PAYER_BANK_ACCOUNT,
        path: "payer-accounts",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsPayerBankAccountView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.SETTINGS,
        path: "settings",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsSettingsView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.EXPORT_AND_INTEGRATION,
        path: "export-and-integration",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsExportAndIntegrationView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.COMMISSIONS_AND_DISCOUNTS,
        path: "commissions-and-discount",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsCommissionsAndDiscountsView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.EDITING_ATTRIBUTES,
        path: "editing-attributes",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsEditAttributesView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.PROJECT_SETTING,
        path: "project-settings",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsProjectSettingView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.CONTRACTOR_CODES,
        path: "contractor-codes",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsCodesView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.AUTO_INVOICE,
        path: "auto-invoice",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsAutoInvoiceView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.STANDING_ORDERS,
        path: "standing-orders",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsStandingOrdersView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.MAIL_NOTIFICATION,
        path: "mail-notification",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsMailNotificationView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.CUSTOM_EMAIL_CONTENT,
        path: "custom-email-content",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsCustomMailContentView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.DOCUMENT_REGISTER,
        path: "document-register",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsDocumentRegisterView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.BRANCH,
        path: "branch",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsBranchView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.ATTRIBUTE_MAPPING,
        path: "attribute-mapping",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsAttributeMappingView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.CONTRACTOR.KSEF,
        path: "attribute-mapping",
        component: () =>
          import(
            /* webpackChunkName: "administration-contractors" */ "@/views/administration/contractors/AdministrationContractorsKsefConfigurationView.vue"
          ),
      },
    ],
  },
  {
    name: "Administration Booking Service",
    path: "administration/booking-service",
    component: () =>
      import(/* webpackChunkName: "administration" */ "@/views/administration/AdministrationBookingServiceView.vue"),
    meta: { hasOneOfPermissions: [PERMISSIONS.BookingItemServiceRead] },
  },
  {
    name: "Administration Bank Accounts",
    path: "administration/bank-accounts",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/views/administration/bankAccounts/AdministrationBankAccountsView.vue"
      ),
  },
  {
    name: "Administration Bank Accounts Assign",
    path: "administration/bank-accounts-assign",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/views/administration/bankAccounts/AdministrationBankAccountsAssignView.vue"
      ),
  },
  {
    name: "Credit Cards",
    path: "administration/bank-accounts-credit-cards",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/views/administration/bankAccounts/AdministrationBankAccountsCreditCardsView.vue"
      ),
  },
  {
    name: "User Credit Cards",
    path: "administration/bank-accounts-user-credit-cards",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/views/administration/bankAccounts/AdministrationBankAccountsUserCreditCardsView.vue"
      ),
  },
  {
    name: "Administration Exchange Rate",
    path: "administration/exchange-rate",
    component: () =>
      import(/* webpackChunkName: "administration" */ "@/views/administration/AdministrationExchangeRateView.vue"),
    meta: { hasOneOfPermissions: [PERMISSIONS.ExchangeRateProviderRead] },
  },
  {
    name: "Administration Printers",
    path: "administration/printers",
    component: () =>
      import(/* webpackChunkName: "administration" */ "@/views/administration/printers/PrintersView.vue"),
    meta: { hasOneOfPermissions: [PERMISSIONS.AdministrationPrintersRead] },
  },
  {
    name: "Administration User Tasks",
    path: "administration/user-tasks",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/views/administration/taskScheduler/AdministrationUserTasksView.vue"
      ),
  },
  {
    name: "Administration Task Scheduler",
    path: "administration/task-scheduler",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/views/administration/taskScheduler/AdministrationTaskSchedulerView.vue"
      ),
  },
  {
    name: "Administration Offices",
    path: "administration/offices",
    component: () =>
      import(/* webpackChunkName: "administration" */ "@/views/administration/offices/AdministrationOfficesView.vue"),
    redirect: { name: ADMINISTRATION_ROUTE_NAMES.OFFICES.BASIC_INFO },
    children: [
      {
        name: ADMINISTRATION_ROUTE_NAMES.OFFICES.BASIC_INFO,
        path: "basic",
        component: () =>
          import(
            /* webpackChunkName: "administration-offices" */ "@/views/administration/offices/officesTabs/AdministrationOfficesBasicInfoTabView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.OFFICES.ADDRESS,
        path: "address",
        component: () =>
          import(
            /* webpackChunkName: "administration-offices" */ "@/views/administration/offices/officesTabs/AdministrationOfficesAddressTabView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.OFFICES.SETTINGS,
        path: "settings",
        component: () =>
          import(
            /* webpackChunkName: "administration-offices" */ "@/views/administration/offices/officesTabs/AdministrationOfficesSettingsTabView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.OFFICES.OFFICE_PROFILES,
        path: "office-profile",
        component: () =>
          import(
            /* webpackChunkName: "administration-offices" */ "@/views/administration/offices/officesTabs/AdministrationOfficesOfficeProfileTabView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.OFFICES.DOCUMENT_REGISTERS,
        path: "document-registers",
        component: () =>
          import(
            /* webpackChunkName: "administration-offices" */ "@/views/administration/offices/officesTabs/AdministrationOfficesDocumentRegisterTabView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.OFFICES.CASH_REGISTERS,
        path: "cash-registers",
        component: () =>
          import(
            /* webpackChunkName: "administration-offices" */ "@/views/administration/offices/officesTabs/AdministrationOfficesCashRegisterTabView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.OFFICES.PAYMENTS,
        path: "payments",
        component: () =>
          import(
            /* webpackChunkName: "administration-offices" */ "@/views/administration/offices/officesTabs/AdministrationOfficesPaymentsTabView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.OFFICES.CASHIERS,
        path: "cashiers",
        component: () =>
          import(
            /* webpackChunkName: "administration-offices" */ "@/views/administration/offices/officesTabs/AdministrationOfficesCashiersTabView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.OFFICES.TRADERS,
        path: "traders",
        component: () =>
          import(
            /* webpackChunkName: "administration-offices" */ "@/views/administration/offices/officesTabs/AdministrationOfficesTradersTabView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.OFFICES.SIGNATURES,
        path: "signatures",
        component: () =>
          import(
            /* webpackChunkName: "administration-offices" */ "@/views/administration/offices/officesTabs/AdministrationOfficesSignaturesTabView.vue"
          ),
      },
      {
        name: ADMINISTRATION_ROUTE_NAMES.OFFICES.REPORTS,
        path: "reports",
        component: () =>
          import(
            /* webpackChunkName: "administration-offices" */ "@/views/administration/offices/officesTabs/AdministrationOfficesReportsTabView.vue"
          ),
      },
    ],
  },
  {
    name: "Administration User Profile",
    path: "administration/user-profile",
    component: () =>
      import(
        /* webpackChunkName: "administration-offices" */ "@/views/administration/AdministrationUserProfileView.vue"
      ),
  },
  {
    name: "Administration Mail Box Mail History",
    path: "administration/mail-box/mail-history",
    component: () =>
      import(
        /* webpackChunkName: "administration-mail-box" */ "@/views/administration/mailBox/AdministrationMailBoxMailHistoryView.vue"
      ),
  },
  {
    name: "Administration Mail Box Configuration",
    path: "administration/mail-box/configuration",
    component: () =>
      import(
        /* webpackChunkName: "administration-mail-box" */ "@/views/administration/mailBox/AdministrationMailBoxConfigurationView.vue"
      ),
  },
  {
    name: "Administration Mail Box Mail Content",
    path: "administration/mail-box/mail-content",
    component: () =>
      import(
        /* webpackChunkName: "administration-mail-box" */ "@/views/administration/mailBox/AdministrationMailBoxMailContentView.vue"
      ),
  },
  {
    name: "Administration Mail Box Booking Files",
    path: "administration/mail-box/booking-files",
    component: () =>
      import(
        /* webpackChunkName: "administration-mail-box" */ "@/views/administration/mailBox/AdministrationMailBoxBookingFilesView.vue"
      ),
  },
  {
    name: "Administration Integration Configuration",
    path: "administration/integration-configuration",
    component: () =>
      import(
        /* webpackChunkName: "administration-mail-box" */ "@/views/administration/AdministrationIntegrationView.vue"
      ),
  },
  {
    name: "Administration KSEF Certificate",
    path: "administration/ksef-certificate",
    component: () =>
      import(/* webpackChunkName: "administration" */ "@/views/administration/AdministrationKsefCertView.vue"),
  },
  {
    name: "Onyx fix",
    path: "administration/onyx-fix",
    component: () =>
      import(
        /* webpackChunkName: "administration-onyx-fix" */ "@/views/administration/AdministrationKsefOnyxFixView.vue"
      ),
  },
  {
    name: "Administration Payment Methods",
    path: "administration/paymentmethods",
    component: () =>
      import(
        /* webpackChunkName: "administration-mail-box" */ "@/views/administration/paymentMethods/AdministrationPaymentMethodsView.vue"
      ),
  },
  {
    name: "Administration Payment Methods Types",
    path: "administration/paymentmethodstypes",
    component: () =>
      import(
        /* webpackChunkName: "administration-mail-box" */ "@/views/administration/paymentMethods/AdministrationPaymentMethodsTypesView.vue"
      ),
  },
  {
    name: "Airlines",
    path: "administration/airlines",
    component: () =>
      import(/* webpackChunkName: "administration-mail-box" */ "@/views/administration/AdministrationAirlinesView.vue"),
  },
  {
    name: "Administration Work Flow",
    path: "administration/work-flow",
    component: () =>
      import(
        /* webpackChunkName: "administration-workflow" */ "@/views/administration/workflow/AdministrationWorkFlowView.vue"
      ),
  },
  {
    name: "Administration Work Flow Common",
    path: "administration/work-flow-common",
    component: () =>
      import(
        /* webpackChunkName: "administration-workflow" */ "@/views/administration/workflow/AdministrationWorkFlowCommonView.vue"
      ),
  },
  {
    name: "Administration Export Configuration Bank Account",
    path: "administration/export-configuration-bank-account",
    component: () =>
      import(
        /* webpackChunkName: "administration-export-configuration" */ "@/views/administration/exportConfiguration/AdministrationExportConfigurationBankAccountView.vue"
      ),
  },
  {
    name: "Administration Export Configuration Order",
    path: "administration/export-configuration-order",
    component: () =>
      import(
        /* webpackChunkName: "administration-export-configuration" */ "@/views/administration/exportConfiguration/AdministrationExportConfigurationOrderView.vue"
      ),
  },
  {
    name: "Administration Export Configuration Document Type Register",
    path: "administration/export-configuration-document-type-register",
    component: () =>
      import(
        /* webpackChunkName: "administration-export-configuration" */ "@/views/administration/exportConfiguration/AdministrationExportConfigurationDocumentTypeRegisterView.vue"
      ),
  },
  {
    name: "Administration GDS",
    path: "administration/gds",
    component: () =>
      import(/* webpackChunkName: "administration-gds" */ "@/views/administration/gds/AdministrationGDSView.vue"),
  },
  {
    name: "Administration GDS Settings",
    path: "administration/gds-settings",
    component: () =>
      import(
        /* webpackChunkName: "administration-gds" */ "@/views/administration/gds/AdministrationGDSSettingsView.vue"
      ),
  },
];

export { AdministrationRoutes };
export default AdministrationRoutes;
